// src/components/ContractsViewer.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { TextStyle } from '@tiptap/extension-text-style';
import { TextAlign } from '@tiptap/extension-text-align';
import { Extension } from '@tiptap/core';
import { Node } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import Mustache from 'mustache';
import { fetchAuthSession } from 'aws-amplify/auth';
import { brandIdentity } from '../theme/brandIdentity';
import Logo from './Logo';

// Variable catalog from ContractEditor
const VARIABLE_CATALOG = {
  'Cliente (Customer)': [
    { label: 'Cédula / ID', token: 'customer.id' },
    { label: 'Nombre completo', token: 'customer.full_name' },
    { label: 'Teléfono', token: 'customer.phone' },
    { label: 'Email', token: 'customer.email' },
    { label: 'Dirección', token: 'customer.address' },
    { label: 'Ciudad', token: 'customer.city' },
    { label: 'Firma (imagen)', token: 'customer.signature_image' }
  ],
  'Transacción (Transaction)': [
    { label: 'Número de transacción', token: 'transaction.id' },
    { label: 'Fecha (YYYY-MM-DD)', token: 'transaction.date' },
    { label: 'Hora (HH:mm)', token: 'transaction.time' }
  ],
  'Dispositivo (Device)': [
    { label: 'Marca', token: 'device.brand' },
    { label: 'Nombre/Modelo', token: 'device.name' },
    { label: 'Capacidad (GB)', token: 'device.storage' },
    { label: 'Color', token: 'device.color' },
    { label: 'IMEI 1', token: 'device.imei1' },
    { label: 'IMEI 2', token: 'device.imei2' },
    { label: 'IMEI 1 - Cód. Barras', token: 'device.imei1_barcode' },
    { label: 'IMEI 2 - Cód. Barras', token: 'device.imei2_barcode' },
    { label: 'Grado del dispositivo', token: 'device.grade' }
  ],
  'Comprador (trader)': [
    { label: 'Nombre legal', token: 'trader.name' },
    { label: 'NIT/RFC', token: 'trader.tax_id' },
    { label: 'Teléfono', token: 'trader.phone' },
    { label: 'Email', token: 'trader.email' },
    { label: 'Dirección', token: 'trader.address' },
    { label: 'Ciudad', token: 'trader.city' }
  ],
  'Tienda (Store)': [
    { label: 'Nombre de la tienda', token: 'store.name' },
    { label: 'Ciudad', token: 'store.city' }
  ],
  'Venta (Sold)': [
    { label: 'Dispositivo vendido', token: 'sold.device' },
    { label: 'Precio del dispositivo vendido', token: 'sold.price' }
  ],
  'Montos (Amounts)': [
    { label: 'Bono oferta comprador', token: 'amounts.bonus_trader' },
    { label: 'Bono adicional marca', token: 'amounts.bonus_brand' },
    { label: 'Total', token: 'amounts.total' }
  ],
  'Asesor (Advisor)': [
    { label: 'Nombre', token: 'advisor.name' },
    { label: 'ID', token: 'advisor.id' }
  ],
  'Códigos SKU (SKU Codes)': [
    { label: 'SKU Bono del comprador - Valor', token: 'codes.bonus_trader_value' },
    { label: 'SKU Bono de la marca - Valor', token: 'codes.bonus_brand_value' },
    { label: 'SKU Bono del comprador - Cód. Barras', token: 'codes.bonus_trader_barcode' },
    { label: 'SKU Bono de la marca - Cód. Barras', token: 'codes.bonus_brand_barcode' }
  ],
  'Firmas/Fotos (Signatures/Photos)': [
    { label: 'Imagen anverso doc.', token: 'photos.front_image' },
    { label: 'Imagen reverso doc.', token: 'photos.back_image' }
  ]
};

// Custom Page Break Node Extension
const PageBreak = Node.create({
  name: 'pageBreak',
  
  group: 'block',
  
  atom: true,
  
  selectable: true,
  
  parseHTML() {
    return [
      {
        tag: 'div[data-page-break]',
      },
    ];
  },
  
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      {
        'data-page-break': 'true',
        'class': 'page-break',
        'contentEditable': false,
      },
      '📄 SALTO DE PÁGINA',
    ];
  },
  
  addNodeView() {
    return ({ node, HTMLAttributes }) => {
      const div = document.createElement('div');
      div.setAttribute('data-page-break', 'true');
      div.className = 'page-break';
      div.contentEditable = false;
      div.innerHTML = `
        <div style="
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 16px;
          margin: 32px 0;
          border-top: 2px dashed #6366F1;
          border-bottom: 2px dashed #6366F1;
          background: #F0F9FF;
          color: #6366F1;
          font-weight: 600;
          font-size: 14px;
          border-radius: 4px;
          position: relative;
          user-select: none;
        ">
          <span>📄</span>
          <span>SALTO DE PÁGINA</span>
          <button 
            style="
              position: absolute;
              top: 8px;
              right: 8px;
              background: #DC2626;
              color: white;
              border: none;
              border-radius: 50%;
              width: 20px;
              height: 20px;
              cursor: pointer;
              font-size: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0.8;
            "
            onclick="this.closest('[data-page-break]').remove()"
            title="Eliminar salto de página"
          >×</button>
        </div>
      `;
      
      return {
        dom: div,
      };
    };
  },
  
  addCommands() {
    return {
      insertPageBreak: () => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
        });
      },
    };
  },
});

// Custom Variable Node Extension
const Variable = Node.create({
  name: 'variable',
  
  group: 'inline',
  
  inline: true,
  
  atom: true,
  
  addAttributes() {
    return {
      key: {
        default: null,
      },
      label: {
        default: null,
      },
    };
  },
  
  parseHTML() {
    return [
      {
        tag: 'span[data-variable]',
        getAttrs: dom => ({
          key: dom.getAttribute('data-variable'),
          label: dom.textContent.replace(/[{}]/g, ''),
        }),
      },
    ];
  },
  
  renderHTML({ HTMLAttributes, node }) {
    const { key, label } = node.attrs;
    return [
      'span',
      {
        'data-variable': key,
        'class': 'variable-chip',
        'contentEditable': false,
      },
      `{{${key}}}`,
    ];
  },
  
  addNodeView() {
    return ({ node, HTMLAttributes }) => {
      const span = document.createElement('span');
      span.setAttribute('data-variable', node.attrs.key);
      span.className = 'variable-chip';
      span.contentEditable = false;
      span.draggable = true;
      span.textContent = `{{${node.attrs.key}}}`;
      span.title = `Variable: ${node.attrs.key}`;
      
      return {
        dom: span,
      };
    };
  },
});

// Font Size Extension
const FontSize = Extension.create({
  name: 'fontSize',

  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize.replace('px', ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}px`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run();
      },
    };
  },
});

// Suggestion Plugin for autocomplete
const suggestionPluginKey = new PluginKey('suggestion');

const SuggestionPlugin = (options) => {
  return new Plugin({
    key: suggestionPluginKey,
    
    state: {
      init() {
        return {
          active: false,
          range: null,
          query: '',
        };
      },
      
      apply(tr, value, oldState, newState) {
        const { selection } = newState;
        const next = { ...value };
        
        if (selection.from !== selection.to) {
          next.active = false;
          next.range = null;
          next.query = '';
          return next;
        }
        
        const $position = selection.$from;
        const textBefore = $position.nodeBefore?.text || '';
        
        // Check for trigger characters (/ or @)
        const triggerMatch = textBefore.match(/[/@]([^/@ ]*)$/);
        
        if (triggerMatch && !triggerMatch[1].includes(' ')) {
          const query = triggerMatch[1];
          const range = {
            from: $position.pos - triggerMatch[0].length,
            to: $position.pos,
          };
          
          next.active = true;
          next.range = range;
          next.query = query;
          
          // Call the onUpdate callback
          if (options.onUpdate) {
            options.onUpdate(next);
          }
        } else {
          next.active = false;
          next.range = null;
          next.query = '';
          
          if (options.onUpdate) {
            options.onUpdate(next);
          }
        }
        
        return next;
      },
    },
    
    props: {
      decorations(state) {
        const { active, range } = this.getState(state);
        
        if (!active) return null;
        
        return DecorationSet.create(state.doc, [
          Decoration.inline(range.from, range.to, {
            class: 'suggestion-highlight',
          }),
        ]);
      },
    },
  });
};

const ContractsViewer = () => {
  const navigate = useNavigate();
  
  // Contract list states
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingContractInfo, setLoadingContractInfo] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  
  // Contract details states
  const [contractInfo, setContractInfo] = useState(null);
  const [contractTemplate, setContractTemplate] = useState(null);
  
  // Editor states
  const [contractName, setContractName] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionQuery, setSuggestionQuery] = useState('');
  const [suggestionRange, setSuggestionRange] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [showTableSizeSelector, setShowTableSizeSelector] = useState(false);
  const [cursorInTable, setCursorInTable] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editMode, setEditMode] = useState('view'); // 'view', 'edit', 'create'
  
  // Collapsible details state
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  
  // API Error state
  const [apiError, setApiError] = useState('');
  
  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalContent, setOriginalContent] = useState(null);
  const [originalName, setOriginalName] = useState('');
  
  // Contract assignment modal state
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  
  // Image upload states
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingToS3, setUploadingToS3] = useState(false);
  
  // Sample data for preview
  const [sampleData, setSampleData] = useState({
    customer: {
      id: '12345678',
      full_name: 'Juan Pérez García',
      phone: '+57 300 123 4567',
      email: 'juan.perez@email.com',
      address: 'Calle 123 #45-67',
      city: 'Bogotá',
      signature_image: '[Imagen de firma]'
    },
    transaction: {
      id: 'TXN-2024-001',
      date: '2024-01-15',
      time: '14:30'
    },
    device: {
      brand: 'Apple',
      name: 'iPhone 14 Pro',
      storage: '256',
      color: 'Azul Sierra',
      imei1: '123456789012345',
      imei2: '543210987654321',
      imei1_barcode: '[Código de barras IMEI1]',
      imei2_barcode: '[Código de barras IMEI2]',
      grade: 'A+'
    },
    trader: {
      name: 'Retoma Corp',
      tax_id: '900123456-7',
      phone: '+57 1 234 5678',
      email: 'info@retoma.com',
      address: 'Carrera 7 #123-45',
      city: 'Bogotá'
    },
    store: {
      name: 'Tienda Centro',
      city: 'Bogotá'
    },
    sold: {
      device: 'iPhone 13',
      price: '$2,500,000'
    },
    amounts: {
      bonus_trader: '$300,000',
      bonus_brand: '$200,000',
      total: '$500,000'
    },
    advisor: {
      name: 'María González',
      id: 'ADV001'
    },
    codes: {
      bonus_trader_value: 'SKU123',
      bonus_brand_value: 'SKU456',
      bonus_trader_barcode: '[Código SKU123]',
      bonus_brand_barcode: '[Código SKU456]'
    },
    photos: {
      front_image: '[Imagen frontal documento]',
      back_image: '[Imagen posterior documento]'
    }
  });

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      FontSize,
      Variable,
      PageBreak,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      SuggestionPlugin({
        onUpdate: ({ active, query, range }) => {
          setShowSuggestions(active);
          setSuggestionQuery(query);
          setSuggestionRange(range);
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setCursorInTable(editor.isActive('table'));
      // Check for unsaved changes
      if (editMode === 'edit' || editMode === 'create') {
        // Use setTimeout to avoid infinite loops during content setting
        setTimeout(() => {
          checkForUnsavedChanges(editor);
        }, 100);
      }
    },
    onSelectionUpdate: ({ editor }) => {
      setCursorInTable(editor.isActive('table'));
    },
  });

  // Function to get auth headers
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }
      
      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // Function to check for unsaved changes
  const checkForUnsavedChanges = (editorInstance = editor) => {
    if (!editorInstance || editMode === 'view') {
      setHasUnsavedChanges(false);
      return;
    }
    
    const currentContent = editorInstance.getJSON();
    const currentName = contractName.trim();
    
    // Compare content and name with original values
    const contentChanged = JSON.stringify(currentContent) !== JSON.stringify(originalContent);
    const nameChanged = currentName !== originalName;
    
    setHasUnsavedChanges(contentChanged || nameChanged);
  };
  
  // Function to set original content for change tracking
  const setOriginalState = (content, name) => {
    setOriginalContent(content);
    setOriginalName(name);
    setHasUnsavedChanges(false);
  };
  
  // Function to check unsaved changes before navigation
  const checkUnsavedChanges = () => {
    if (hasUnsavedChanges) {
      return window.confirm(
        '⚠️ Tienes cambios sin guardar\n\n' +
        'Si continúas, se perderán todos los cambios que hayas realizado en este contrato.\n\n' +
        '¿Estás seguro de que deseas continuar sin guardar?'
      );
    }
    return true;
  };
  
  // Function to handle assign contract
  const handleAssignContract = () => {
    if (!checkUnsavedChanges()) {
      return;
    }
    setShowAssignmentModal(true);
  };
  
  // Function to navigate to entities
  const navigateToEntities = () => {
    if (!checkUnsavedChanges()) {
      return;
    }
    navigate('/entidades');
  };

  // Function to fetch contracts list
  const fetchContracts = async () => {
    setLoadingContracts(true);
    setApiError('');
    
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_templates?mode=headers', {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📋 Respuesta de contratos:', data);
      
      if (data.item && data.item.contracts) {
        const contractsList = [];
        const contractsObj = data.item.contracts;
        
        // Process the contracts object
        Object.entries(contractsObj).forEach(([contractId, contractData]) => {
          // Check if data is in DynamoDB format (with .M and .S) or already processed
          if (contractData.M) {
            // DynamoDB format
            const name = contractData.M.name?.S || 'Sin nombre';
            const updatedAt = contractData.M.updated_at?.S || '';
            const lastVersion = contractData.M.last_version?.S || '0001';
            
            contractsList.push({
              id: contractId,
              name,
              updatedAt,
              lastVersion
            });
          } else if (contractData.name) {
            // Already processed format
            const name = contractData.name || 'Sin nombre';
            const updatedAt = contractData.updated_at || '';
            const lastVersion = contractData.last_version || '0001';
            
            contractsList.push({
              id: contractId,
              name,
              updatedAt,
              lastVersion
            });
          }
        });
        
        console.log('📋 Contratos procesados:', contractsList);
        
        // Sort by name
        contractsList.sort((a, b) => a.name.localeCompare(b.name));
        setContracts(contractsList);
      } else {
        setContracts([]);
      }
    } catch (error) {
      console.error('❌ Error al obtener contratos:', error);
      setApiError('Error al cargar los contratos: ' + error.message);
    } finally {
      setLoadingContracts(false);
    }
  };

  // Function to fetch contract info
  const fetchContractInfo = async (contractId, version) => {
    setLoadingContractInfo(true);
    setApiError('');
    
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_templates?mode=info&contract_id=${contractId}&version=${version}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📝 Información del contrato:', data);
      console.log('📝 Campos disponibles:', Object.keys(data.item || {}));
      // Extract the actual contract info from data.item
      setContractInfo(data.item || data);
    } catch (error) {
      console.error('❌ Error al obtener información del contrato:', error);
      setApiError('Error al cargar la información del contrato: ' + error.message);
    } finally {
      setLoadingContractInfo(false);
    }
  };

  // Function to fetch and load contract template
  const fetchContractTemplate = async (contractId, version) => {
    setLoadingTemplate(true);
    setApiError('');
    
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_templates?mode=template&contract_id=${contractId}&version=${version}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📄 Template del contrato:', data);
      
      // Check if the response has the expected format with body and isBase64Encoded
      let templateData;
      
      if (data.body !== undefined) {
        // New format: {statusCode, headers, body, isBase64Encoded}
        let templateContent = data.body;
        
        // Check if content is base64 encoded
        if (data.isBase64Encoded) {
          try {
            templateContent = atob(templateContent);
          } catch (error) {
            console.error('Error decodificando base64:', error);
            throw new Error('Error al decodificar el contenido del template');
          }
        }
        
        // Parse the JSON content
        try {
          templateData = JSON.parse(templateContent);
        } catch (error) {
          console.error('Error parsing JSON:', error);
          throw new Error('Error al parsear el contenido JSON del template');
        }
      } else {
        // Old format: the response is already the template data
        templateData = data;
      }
      
      console.log('📦 Datos del template procesado:', templateData);
      console.log('📦 Estructura del template:', {
        hasContentJson: !!templateData.content_json,
        hasContent: !!templateData.content?.json,
        directContent: !!templateData.json,
        keys: Object.keys(templateData)
      });
      
      // Load template into editor - check multiple possible structures
      let contentToLoad = null;
      if (templateData.content_json?.content?.json) {
        contentToLoad = templateData.content_json.content.json;
      } else if (templateData.content?.json) {
        contentToLoad = templateData.content.json;
      } else if (templateData.json) {
        contentToLoad = templateData.json;
      }
      
      if (contentToLoad && editor) {
        editor.commands.setContent(contentToLoad, false);
        setContractName(templateData.name || '');
        
        // Update sample data if available - check multiple possible structures
        let sampleDataToLoad = null;
        if (templateData.content_json?.variables?.sample_data) {
          sampleDataToLoad = templateData.content_json.variables.sample_data;
        } else if (templateData.variables?.sample_data) {
          sampleDataToLoad = templateData.variables.sample_data;
        }
        
        if (sampleDataToLoad) {
          setSampleData(prev => ({
            ...prev,
            ...sampleDataToLoad
          }));
        }
        
        setContractTemplate(templateData);
      }
    } catch (error) {
      console.error('❌ Error al obtener template del contrato:', error);
      setApiError('Error al cargar el template del contrato: ' + error.message);
    } finally {
      setLoadingTemplate(false);
    }
  };

  // Function to handle contract selection
  const handleSelectContract = async (contract) => {
    // Check for unsaved changes before switching contracts
    if (!checkUnsavedChanges()) {
      return;
    }
    
    if (selectedContract?.id === contract.id) {
      // Deselect
      setSelectedContract(null);
      setContractInfo(null);
      setContractTemplate(null);
      if (editor) {
        editor.commands.clearContent();
      }
      setContractName('');
      setEditMode('view');
      setOriginalState(null, '');
    } else {
      // Select new contract
      setSelectedContract(contract);
      setEditMode('view');
      setHasUnsavedChanges(false);
      
      // Fetch contract info and template
      await Promise.all([
        fetchContractInfo(contract.id, contract.lastVersion),
        fetchContractTemplate(contract.id, contract.lastVersion)
      ]);
    }
  };

  // Function to handle creating new contract
  const handleCreateNew = () => {
    // Check for unsaved changes before creating new contract
    if (!checkUnsavedChanges()) {
      return;
    }
    
    setSelectedContract(null);
    setContractInfo(null);
    setContractTemplate(null);
    if (editor) {
      editor.commands.clearContent();
    }
    setContractName('');
    setEditMode('create');
    setOriginalState(null, '');
  };

  // Function to handle edit contract
  const handleEditContract = () => {
    console.log('🔧 handleEditContract called');
    console.log('🔧 selectedContract:', selectedContract);
    console.log('🔧 contractTemplate:', contractTemplate);
    
    if (!selectedContract || !contractTemplate) {
      alert('No hay contrato seleccionado o no se ha cargado correctamente');
      return;
    }
    
    // Load the contract into the editor for editing
    setEditMode('edit');
    
    // Ensure the template is loaded in the editor - use flexible structure detection
    let contentToLoad = null;
    if (contractTemplate.content_json?.content?.json) {
      contentToLoad = contractTemplate.content_json.content.json;
    } else if (contractTemplate.content?.json) {
      contentToLoad = contractTemplate.content.json;
    } else if (contractTemplate.json) {
      contentToLoad = contractTemplate.json;
    }
    
    if (contentToLoad && editor) {
      editor.commands.setContent(contentToLoad, false);
      const name = contractTemplate.name || selectedContract.name || '';
      setContractName(name);
      
      // Set original state for change tracking
      setOriginalState(contentToLoad, name);
      
      // Update sample data if available - check multiple possible structures
      let sampleDataToLoad = null;
      if (contractTemplate.content_json?.variables?.sample_data) {
        sampleDataToLoad = contractTemplate.content_json.variables.sample_data;
      } else if (contractTemplate.variables?.sample_data) {
        sampleDataToLoad = contractTemplate.variables.sample_data;
      }
      
      if (sampleDataToLoad) {
        setSampleData(prev => ({
          ...prev,
          ...sampleDataToLoad
        }));
      }
    }
  };

  // Function to delete contract
  const deleteContract = async (contractId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_templates', {
        method: 'DELETE',
        headers,
        body: JSON.stringify({
          contract_id: contractId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error del servidor: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Contrato eliminado:', result);
      
      return result;
    } catch (error) {
      console.error('❌ Error al eliminar contrato:', error);
      throw error;
    }
  };

  // Function to handle delete contract
  const handleDeleteContract = async (contract) => {
    const confirmMessage = `¿Estás seguro de que deseas eliminar la plantilla "${contract.name}"?\n\n⚠️ ADVERTENCIA: Esta acción no se puede deshacer y se eliminarán TODAS las versiones del contrato seleccionado.\n\n¿Deseas continuar?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteContract(contract.id);
        
        // Refresh the contracts list
        await fetchContracts();
        
        // Clear selection if deleted contract was selected
        if (selectedContract?.id === contract.id) {
          setSelectedContract(null);
          setContractInfo(null);
          setContractTemplate(null);
          if (editor) {
            editor.commands.clearContent();
          }
          setContractName('');
          setEditMode('view');
        }
        
        alert(`Contrato "${contract.name}" eliminado exitosamente`);
      } catch (error) {
        alert('Error al eliminar el contrato: ' + error.message);
      }
    }
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    // Check for unsaved changes before canceling
    if (hasUnsavedChanges) {
      const confirmCancel = window.confirm(
        '¿Estás seguro de que deseas cancelar?\n\nSe perderán todos los cambios no guardados.'
      );
      if (!confirmCancel) {
        return;
      }
    }
    
    if (selectedContract) {
      setEditMode('view');
      // Reload the template
      fetchContractTemplate(selectedContract.id, selectedContract.lastVersion);
    } else {
      setEditMode('view');
      if (editor) {
        editor.commands.clearContent();
      }
      setContractName('');
      setOriginalState(null, '');
    }
  };

  // Listen for beforeunload to warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // For legacy browsers
        return '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Load contracts on component mount
  useEffect(() => {
    fetchContracts();
  }, []);

  // Editor helper functions (from ContractEditor)
  const getAllVariables = () => {
    const allVars = [];
    Object.values(VARIABLE_CATALOG).forEach(category => {
      category.forEach(variable => {
        allVars.push(variable);
      });
    });
    return allVars;
  };

  const getFilteredVariables = (query) => {
    if (!query) return getAllVariables();
    
    return getAllVariables().filter(variable =>
      variable.label.toLowerCase().includes(query.toLowerCase()) ||
      variable.token.toLowerCase().includes(query.toLowerCase())
    );
  };

  const insertVariable = (token) => {
    if (!editor || !suggestionRange) return;
    
    const label = getAllVariables().find(v => v.token === token)?.label || token;
    
    editor
      .chain()
      .focus()
      .deleteRange(suggestionRange)
      .insertContent({
        type: 'variable',
        attrs: { key: token, label },
      })
      .run();
    
    setShowSuggestions(false);
  };

  const insertVariableFromSidebar = (token) => {
    if (!editor) return;
    
    const label = getAllVariables().find(v => v.token === token)?.label || token;
    
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'variable',
        attrs: { key: token, label },
      })
      .run();
  };

  // Table manipulation functions
  const addTableRow = () => {
    if (!editor) return;
    editor.chain().focus().addRowAfter().run();
  };

  const deleteTableRow = () => {
    if (!editor) return;
    editor.chain().focus().deleteRow().run();
  };

  const addTableColumn = () => {
    if (!editor) return;
    editor.chain().focus().addColumnAfter().run();
  };

  const deleteTableColumn = () => {
    if (!editor) return;
    editor.chain().focus().deleteColumn().run();
  };

  const deleteTable = () => {
    if (!editor) return;
    if (window.confirm('¿Estás seguro de que quieres eliminar la tabla completa?')) {
      editor.chain().focus().deleteTable().run();
    }
  };

  const canManipulateTable = () => {
    return cursorInTable;
  };

  const setFontSize = (size) => {
    if (!editor) return;
    editor.chain().focus().setFontSize(size).run();
  };

  const insertPageBreak = () => {
    if (!editor) return;
    editor.chain().focus().insertContent({
      type: 'pageBreak'
    }).run();
  };

  const insertTable = (rows = 3, cols = 3, withHeader = true) => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: withHeader }).run();
    setShowTableSizeSelector(false);
  };

  const insertTableWithSize = () => {
    setShowTableSizeSelector(!showTableSizeSelector);
  };

  const TABLE_PRESETS = [
    { name: '2x2 Simple', rows: 2, cols: 2, header: false },
    { name: '3x2 Lista', rows: 3, cols: 2, header: true },
    { name: '3x3 Estándar', rows: 3, cols: 3, header: true },
    { name: '4x3 Amplia', rows: 4, cols: 3, header: true },
    { name: '5x2 Vertical', rows: 5, cols: 2, header: true },
    { name: '2x4 Horizontal', rows: 2, cols: 4, header: true },
    { name: '3x4 Grande', rows: 3, cols: 4, header: true },
    { name: '5x5 Máxima', rows: 5, cols: 5, header: true },
  ];

  // Image Upload Utility Functions
  const validateImageFile = (file) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    
    if (!validTypes.includes(file.type)) {
      alert('Solo se permiten archivos PNG, JPG y SVG');
      return false;
    }
    
    if (file.size > maxSize) {
      alert('El archivo no puede superar los 5MB');
      return false;
    }
    
    return true;
  };

  const compressImageToWebP = (file, quality = 0.8) => {
    return new Promise((resolve) => {
      // If it's already SVG, don't compress
      if (file.type === 'image/svg+xml') {
        resolve(file);
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions (max 800px width/height while maintaining aspect ratio)
        const maxDimension = 800;
        let { width, height } = img;
        
        if (width > height && width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(resolve, 'image/webp', quality);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const generateImagePlaceholder = (index) => {
    return `custom_image_${index + 1}`;
  };

  const handleImageUpload = async (files) => {
    setUploadingImages(true);
    const validFiles = Array.from(files).filter(validateImageFile);
    
    if (validFiles.length === 0) {
      setUploadingImages(false);
      return;
    }

    try {
      const processedImages = [];
      
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const compressedFile = await compressImageToWebP(file);
        const imageDataUrl = URL.createObjectURL(compressedFile);
        
        const imageData = {
          id: Date.now() + i,
          fileName: file.name,
          placeholder: generateImagePlaceholder(uploadedImages.length + i),
          dataUrl: imageDataUrl,
          size: compressedFile.size,
          originalSize: file.size,
          type: compressedFile.type
        };
        
        processedImages.push(imageData);
      }
      
      setUploadedImages(prev => [...prev, ...processedImages]);
    } catch (error) {
      console.error('Error processing images:', error);
      alert('Error al procesar las imágenes');
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (imageId) => {
    setUploadedImages(prev => {
      const updated = prev.filter(img => img.id !== imageId);
      // Clean up object URLs to prevent memory leaks
      const imageToRemove = prev.find(img => img.id === imageId);
      if (imageToRemove && imageToRemove.dataUrl) {
        URL.revokeObjectURL(imageToRemove.dataUrl);
      }
      return updated;
    });
  };

  const insertImagePlaceholder = (placeholder) => {
    if (!editor) return;
    
    // Insert as a variable node, just like regular variables
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'variable',
        attrs: { key: placeholder, label: placeholder },
      })
      .run();
  };

  // Preview generation
  const generatePreview = () => {
    if (!editor) return '';
    
    let html = editor.getHTML();
    
    try {
      html = html.replace(/<span[^>]*data-variable="([^"]*)"[^>]*>{{([^}]*)}}<\/span>/g, '{{$1}}');
      html = Mustache.render(html, sampleData);
    } catch (error) {
      console.error('Error rendering preview:', error);
    }
    
    return html;
  };

  // Function to update contract
  const updateContract = async (contractId, name, contentJson, imageData = null) => {
    try {
      const headers = await getAuthHeaders();
      
      const payload = {
        contract_id: contractId,
        name: name.trim(),
        content_json: contentJson
      };

      // Add image metadata if images were uploaded
      if (imageData && imageData.imagesCount > 0) {
        payload.quantity_images = imageData.imagesCount;
        payload.images_ext = imageData.imagesExt;
        
        // Add upload_token if using staging (for new versions)
        if (imageData.uploadToken) {
          payload.upload_token = imageData.uploadToken;
        }
      }

      console.log('🔄 Actualizando contrato:', payload);

      const response = await fetch('https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_templates', {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error del servidor: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Contrato actualizado:', result);
      
      return result;
    } catch (error) {
      console.error('❌ Error al actualizar contrato:', error);
      throw error;
    }
  };

  // Save contract function
  const saveContract = async () => {
    if (!editor || !contractName.trim()) {
      alert('Por favor ingresa un nombre para el contrato');
      return;
    }

    try {
      const headers = await getAuthHeaders();
      const json = editor.getJSON();
      
      if (editMode === 'edit' && selectedContract) {
        // Show confirmation for updates
        const confirmMessage = `¿Estás seguro de que deseas actualizar la plantilla "${selectedContract.name}"?\n\nSe creará una nueva versión del contrato.`;
        
        if (!window.confirm(confirmMessage)) {
          return;
        }

        // Upload images first if any
        const uploadResult = await uploadAllImagesToS3();
        if (!uploadResult.success) {
          alert('Error al subir imágenes: ' + uploadResult.error);
          return;
        }

        // Update existing contract
        const contentJson = {
          content: {
            json: json
          },
          variables: { 
            sample_data: sampleData 
          }
        };

        const result = await updateContract(selectedContract.id, contractName, contentJson, uploadResult);
        
        // Refresh contracts list
        await fetchContracts();
        
        // Switch to view mode and refresh the selected contract with new version
        setEditMode('view');
        
        // Update selected contract with new version info
        if (result.version) {
          setSelectedContract(prev => ({
            ...prev,
            lastVersion: result.version,
            name: result.name || prev.name
          }));
          
          // Reload contract info and template with new version
          await Promise.all([
            fetchContractInfo(selectedContract.id, result.version),
            fetchContractTemplate(selectedContract.id, result.version)
          ]);
        }
        
        // Clear unsaved changes after successful update
        setHasUnsavedChanges(false);
        
        // Clear uploaded images after successful save
        setUploadedImages([]);
        
        alert(`Contrato actualizado exitosamente. Nueva versión: v${result.version}`);
      } else {
        // Upload images first if any
        const uploadResult = await uploadAllImagesToS3();
        if (!uploadResult.success) {
          alert('Error al subir imágenes: ' + uploadResult.error);
          return;
        }

        // Create new contract
        const payload = {
          name: contractName.trim(),
          locale: "es-CO",
          content_json: {
            content: {
              json: json
            },
            variables: { 
              sample_data: sampleData 
            }
          }
        };

        // Add image metadata if images were uploaded
        if (uploadResult.imagesCount > 0) {
          payload.quantity_images = uploadResult.imagesCount;
          payload.images_ext = uploadResult.imagesExt;
          payload.upload_token = uploadResult.uploadToken;
        }

        console.log('💾 Guardando contrato:', payload);

        const response = await fetch('https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_templates', {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error del servidor: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        console.log('✅ Contrato guardado:', result);
        
        // Refresh contracts list
        await fetchContracts();
        
        // Switch to view mode and clear unsaved changes
        setEditMode('view');
        setHasUnsavedChanges(false);
        
        // Clear uploaded images after successful save
        setUploadedImages([]);
        
        alert('Contrato guardado exitosamente');
      }
    } catch (error) {
      console.error('❌ Error al guardar contrato:', error);
      alert('Error al guardar el contrato: ' + error.message);
    }
  };

  // Image Upload API Functions
  const getPresignedUrls = async (count, extension = 'png', contractId = null) => {
    try {
      const headers = await getAuthHeaders();
      let url = `https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_templates?mode=presigned_url&count=${count}&ext=${extension}`;
      
      if (contractId) {
        url += `&contract_id=${contractId}`;
      }
      
      console.log('🔗 Obteniendo URLs presignadas:', { count, extension, contractId });
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error del servidor: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ URLs presignadas obtenidas:', result);
      return result;
    } catch (error) {
      console.error('❌ Error al obtener URLs presignadas:', error);
      throw error;
    }
  };

  const uploadImageToS3 = async (presignedUrl, file) => {
    try {
      console.log('📤 Subiendo imagen a S3:', presignedUrl);
      
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!response.ok) {
        throw new Error(`Error al subir imagen: ${response.status}`);
      }

      console.log('✅ Imagen subida exitosamente a S3');
      return true;
    } catch (error) {
      console.error('❌ Error al subir imagen a S3:', error);
      throw error;
    }
  };

  const uploadAllImagesToS3 = async () => {
    if (uploadedImages.length === 0) {
      return { success: true, uploadToken: null, imagesCount: 0, imagesExt: 'png' };
    }

    setUploadingToS3(true);
    try {
      console.log('🚀 Iniciando subida de imágenes:', uploadedImages.length);
      
      // Determine the extension (use the first image's extension or default to webp)
      const firstImageExt = uploadedImages[0].type === 'image/svg+xml' ? 'svg' : 'webp';
      
      // Get presigned URLs
      const presignedData = await getPresignedUrls(
        uploadedImages.length, 
        firstImageExt, 
        selectedContract?.contract_id || null
      );

      // Upload each image to its presigned URL
      for (let i = 0; i < uploadedImages.length; i++) {
        const image = uploadedImages[i];
        const urlData = presignedData.urls[i]; // Changed from put_urls to urls
        const presignedUrl = urlData.put_url;
        
        // Convert the blob URL back to a file for upload
        const response = await fetch(image.dataUrl);
        const blob = await response.blob();
        
        await uploadImageToS3(presignedUrl, blob);
      }

      return {
        success: true,
        uploadToken: presignedData.upload_token || null,
        imagesCount: uploadedImages.length,
        imagesExt: firstImageExt
      };
      
    } catch (error) {
      console.error('❌ Error en subida masiva de imágenes:', error);
      return { success: false, error: error.message };
    } finally {
      setUploadingToS3(false);
    }
  };

  if (!editor) {
    return <div>Cargando editor...</div>;
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="contracts-viewer-container">
      {/* Header */}
      <header className="header">
        <Logo variant="horizontal" size="medium" />
        <div className="header-title">Gestión de Plantillas de Contratos</div>
        <div className="user-menu">
          <button 
            className="btn btn-outline" 
            onClick={() => {
          if (checkUnsavedChanges()) {
            navigate('/dashboard');
          }
        }}
          >
            Volver al Dashboard
          </button>
        </div>
      </header>

      <div className="container contracts-layout">
        {/* Left Panel: Contracts List */}
        <div className="contracts-list-column card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="column-title">Plantillas de Contratos</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleCreateNew}
              title="Crear Nueva Plantilla"
            >
              <span>+ Crear</span>
            </button>
          </div>

          {loadingContracts && <div className="loading-spinner-small"></div>}
          {apiError && !loadingContracts && <p className="error-message">{apiError}</p>}

          {!loadingContracts && contracts.length === 0 && !apiError && (
            <p className="empty-state">No hay plantillas de contratos registradas.</p>
          )}

          <ul className="contracts-list">
            {contracts.map((contract) => (
              <li
                key={contract.id}
                className={`contract-list-item ${selectedContract?.id === contract.id ? 'active' : ''}`}
              >
                <div className="contract-item-content" onClick={() => handleSelectContract(contract)}>
                  <div className="contract-name">{contract.name}</div>
                  <div className="contract-meta">
                    <span className="contract-version">v{contract.lastVersion}</span>
                    <span className="contract-date">{formatDate(contract.updatedAt)}</span>
                  </div>
                </div>
                <div className="contract-actions">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteContract(contract);
                    }}
                    className="btn-icon btn-icon-danger" 
                    title="Eliminar Plantilla"
                  >
                    🗑️
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Center Panel: Contract Details and Editor */}
        <div className="contract-details-column card">
          {!selectedContract && editMode !== 'create' ? (
            <div className="empty-state-details">
              <span className="icon-lg">📄</span>
              <h3>Selecciona una Plantilla</h3>
              <p>Haz clic en una plantilla de la lista para verla y editarla, o crea una nueva.</p>
            </div>
          ) : (
            <div className="contract-editor-section">
              {/* Contract Info Section */}
              {editMode === 'view' && selectedContract && (
                <div className="contract-info-section">
                  <div className="contract-info-header">
                    <h2 className="contract-title">{selectedContract.name}</h2>
                  </div>
                  
                  {loadingContractInfo ? (
                    <div className="loading-spinner-small"></div>
                  ) : contractInfo ? (
                    <div className="contract-info-details">
                      <div className="contract-info-card">
                        <div 
                          className="info-header collapsible-header"
                          onClick={() => setDetailsCollapsed(!detailsCollapsed)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="info-icon">📋</div>
                          <h4 style={{ 
                            margin: 0, 
                            color: brandIdentity.colors.textDark,
                            fontSize: brandIdentity.typography.sizes.large,
                            fontWeight: brandIdentity.typography.weights.semibold
                          }}>
                            Detalles de la Plantilla
                          </h4>
                          <div className="collapse-icon">
                            {detailsCollapsed ? '🔽' : '🔼'}
                          </div>
                        </div>
                        
                        {!detailsCollapsed && (
                          <div className="info-content collapsible-content">
                          {/* Show only specific fields */}
                          {Object.entries(contractInfo)
                            .filter(([key, value]) => {
                              // Only show these specific fields
                              const allowedFields = ['version', 'created_at', 'created_by', 'updated_at', 'updated_by'];
                              return allowedFields.includes(key) && value != null && value !== '';
                            })
                            .map(([key, value]) => {
                              // Define display properties for each field
                              const fieldConfig = {
                                version: { icon: '🔢', label: 'Versión', format: (v) => `v${v}` },
                                created_at: { icon: '📅', label: 'Fecha de Creación', format: (v) => formatDate(v) },
                                created_by: { icon: '👤', label: 'Creado por', format: (v) => v },
                                updated_by: { icon: '👤', label: 'Actualizado por', format: (v) => v },
                                name: { icon: '📝', label: 'Nombre', format: (v) => v },
                                locale: { icon: '🌍', label: 'Idioma', format: (v) => v === 'es-CO' ? 'Español (Colombia)' : v },
                                updated_at: { icon: '⏰', label: 'Última Actualización', format: (v) => formatDate(v) },
                                contract_id: { icon: '🆔', label: 'ID del Contrato', format: (v) => v },
                                version_padded: { icon: '🏷️', label: 'Versión Completa', format: (v) => v },
                                status: { icon: '📊', label: 'Estado', format: (v) => v === 'created' ? 'Creado' : v },
                                size_bytes: { icon: '📦', label: 'Tamaño', format: (v) => `${(v / 1024).toFixed(1)} KB` },
                                checksum: { icon: '🔐', label: 'Checksum', format: (v) => v.split(':')[1]?.substring(0, 8) + '...' },
                                // Hide these fields as they're not useful for display
                                s3_uri: null,
                                'tenant_id-contract_id': null,
                                // Default config for unknown fields
                                default: { icon: '📋', label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), format: (v) => String(v) }
                              };
                              
                              const config = fieldConfig[key] || fieldConfig.default;
                              
                              // Skip fields that we don't want to show
                              if (config === null) {
                                return null;
                              }
                              
                              // Skip showing name if it's the same as selected contract name
                              if (key === 'name' && value === selectedContract?.name) {
                                return null;
                              }
                              
                              return (
                                <div key={key} className="info-row">
                                  <div className="info-item-modern">
                                    <span className="info-icon-small">{config.icon}</span>
                                    <div className="info-text">
                                      <span className="info-label-modern">{config.label}</span>
                                      <span className="info-value-modern">
                                        {typeof value === 'object' 
                                          ? JSON.stringify(value, null, 2) 
                                          : config.format(value)
                                        }
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          }
                          
                          {/* Show message if no info available */}
                          {Object.keys(contractInfo).length === 0 && (
                            <div className="info-row">
                              <div className="info-item-modern">
                                <span className="info-icon-small">ℹ️</span>
                                <div className="info-text">
                                  <span className="info-label-modern">Estado</span>
                                  <span className="info-value-modern">No hay información adicional disponible</span>
                                </div>
                              </div>
                            </div>
                          )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Editor Section */}
              {(editMode === 'edit' || editMode === 'create') && (
                <div className="editor-controls">
                  <div className="editor-header">
                    <h3>{editMode === 'create' ? 'Crear Nueva Plantilla' : 'Editar Plantilla'}</h3>
                    <div className="editor-actions">
                      <button 
                        className="btn btn-outline"
                        onClick={handleCancelEdit}
                      >
                        Cancelar
                      </button>
                      <button 
                        onClick={() => setPreviewMode(!previewMode)}
                        className="btn btn-outline"
                      >
                        {previewMode ? 'Editar' : 'Vista Previa'}
                      </button>
                      <button 
                        className="btn btn-primary"
                        onClick={saveContract}
                        disabled={!contractName.trim() || uploadingToS3}
                      >
                        {uploadingToS3 ? (
                          <>⏳ Subiendo imágenes...</>
                        ) : (
                          <>💾 Guardar</>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Contract Name Input */}
                  <div className="form-group">
                    <label className="form-label">Nombre de la plantilla</label>
                    <input
                      type="text"
                      value={contractName}
                      onChange={(e) => {
                        setContractName(e.target.value);
                        // Trigger change detection for name changes
                        if (editMode === 'edit' || editMode === 'create') {
                          setTimeout(() => {
                            checkForUnsavedChanges();
                          }, 100);
                        }
                      }}
                      placeholder="Ingresa el nombre de la plantilla..."
                      className="form-input"
                    />
                  </div>
                </div>
              )}

              {/* Quick Actions for View Mode */}
              {editMode === 'view' && selectedContract && contractTemplate && (
                <div className="quick-actions-section">
                  <div className="quick-actions-header">
                    <h3 style={{
                      margin: 0,
                      fontSize: brandIdentity.typography.sizes.large,
                      fontWeight: brandIdentity.typography.weights.semibold,
                      color: brandIdentity.colors.textDark
                    }}>
                      Vista del Contrato
                    </h3>
                    <div className="quick-actions-buttons">
                      <button 
                        className="btn btn-outline btn-sm"
                        onClick={handleEditContract}
                      >
                        ✏️ Editar
                      </button>
                      <button 
                        onClick={() => setPreviewMode(!previewMode)}
                        className="btn btn-outline btn-sm"
                      >
                        {previewMode ? '📝 Editor' : '👁️ Vista Previa'}
                      </button>
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={handleAssignContract}
                        style={{
                          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                          color: 'white',
                          border: 'none',
                          fontWeight: '600'
                        }}
                      >
                        🔗 Asignar Contrato
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* TipTap Editor */}
              {(editMode === 'edit' || editMode === 'create' || (editMode === 'view' && contractTemplate)) && (
                <div className="tiptap-editor-section">
                  {!previewMode && (editMode === 'edit' || editMode === 'create') && (
                    /* Toolbar */
                    <div className="editor-toolbar">
                      {/* Text Formatting */}
                      <button 
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        className={`toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
                        title="Negrita"
                      >
                        <strong>B</strong>
                      </button>
                      <button 
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        className={`toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
                        title="Cursiva"
                      >
                        <em>I</em>
                      </button>
                      <button 
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        className={`toolbar-btn ${editor.isActive('underline') ? 'active' : ''}`}
                        title="Subrayado"
                      >
                        <u>U</u>
                      </button>
                      
                      <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                      
                      {/* Alignment */}
                      <button 
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                        className={`toolbar-btn ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
                        title="Alinear izquierda"
                      >
                        ⬅
                      </button>
                      <button 
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                        className={`toolbar-btn ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
                        title="Centrar"
                      >
                        ⬄
                      </button>
                      <button 
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}
                        className={`toolbar-btn ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
                        title="Alinear derecha"
                      >
                        ➡
                      </button>
                      <button 
                        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                        className={`toolbar-btn ${editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}`}
                        title="Justificar"
                      >
                        ⬌
                      </button>
                      
                      <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                      
                      {/* Lists */}
                      <button 
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        className={`toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
                        title="Lista con viñetas"
                      >
                        •
                      </button>
                      <button 
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        className={`toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
                        title="Lista numerada"
                      >
                        1.
                      </button>
                      
                      <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                      
                      {/* Headings */}
                      <button 
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        className={`toolbar-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`}
                        title="Título 1"
                      >
                        H1
                      </button>
                      <button 
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={`toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`}
                        title="Título 2"
                      >
                        H2
                      </button>
                      <button 
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        className={`toolbar-btn ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`}
                        title="Título 3"
                      >
                        H3
                      </button>
                      
                      <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                      
                      {/* Table */}
                      <div className="table-size-container" style={{ position: 'relative', display: 'inline-block' }}>
                        <button 
                          onClick={insertTableWithSize}
                          className="toolbar-btn"
                          title="Insertar tabla (seleccionar tamaño)"
                        >
                          ⚏
                        </button>
                        
                        {showTableSizeSelector && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: '0',
                            backgroundColor: 'white',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            zIndex: 1000,
                            minWidth: '200px',
                            marginTop: '4px'
                          }}>
                            <div style={{ 
                              padding: '8px 12px', 
                              borderBottom: '1px solid #E5E7EB', 
                              fontSize: '12px', 
                              fontWeight: '600',
                              color: '#374151'
                            }}>
                              Seleccionar tamaño de tabla
                            </div>
                            {TABLE_PRESETS.map((preset, index) => (
                              <div
                                key={index}
                                onClick={() => insertTable(preset.rows, preset.cols, preset.header)}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  color: '#374151',
                                  borderBottom: index < TABLE_PRESETS.length - 1 ? '1px solid #F3F4F6' : 'none',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#F3F4F6';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                }}
                              >
                                <span>{preset.name}</span>
                                <span style={{ fontSize: '11px', color: '#6B7280' }}>
                                  {preset.rows}×{preset.cols} {preset.header ? '+ header' : ''}
                                </span>
                              </div>
                            ))}
                            <div style={{ 
                              padding: '8px 12px', 
                              borderTop: '1px solid #E5E7EB',
                              fontSize: '11px',
                              color: '#6B7280',
                              textAlign: 'center'
                            }}>
                              Puedes agregar/quitar filas y columnas después
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                      
                      {/* Page Break */}
                      <button 
                        onClick={insertPageBreak}
                        className="toolbar-btn"
                        title="Insertar salto de página"
                      >
                        📄
                      </button>
                      
                      {/* Table Controls - Only show when cursor is inside a table */}
                      {canManipulateTable() && (
                        <>
                          <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                          
                          {/* Table manipulation buttons */}
                          <button 
                            onClick={addTableRow}
                            className="toolbar-btn table-control"
                            title="Agregar fila después"
                          >
                            +📋
                          </button>
                          <button 
                            onClick={deleteTableRow}
                            className="toolbar-btn table-control"
                            title="Eliminar fila actual"
                          >
                            -📋
                          </button>
                          <button 
                            onClick={addTableColumn}
                            className="toolbar-btn table-control"
                            title="Agregar columna después"
                          >
                            +📄
                          </button>
                          <button 
                            onClick={deleteTableColumn}
                            className="toolbar-btn table-control"
                            title="Eliminar columna actual"
                          >
                            -📄
                          </button>
                          <button 
                            onClick={deleteTable}
                            className="toolbar-btn table-control"
                            style={{ 
                              backgroundColor: '#FEE2E2',
                              color: '#DC2626',
                              borderColor: '#FECACA'
                            }}
                            title="Eliminar tabla completa"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                      
                      <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                      
                      {/* Font Size */}
                      <select
                        onChange={(e) => setFontSize(e.target.value)}
                        className="toolbar-btn"
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          minWidth: '100px'
                        }}
                        title="Tamaño de fuente"
                      >
                        <option value="">Tamaño</option>
                        <option value="8">8px</option>
                        <option value="10">10px</option>
                        <option value="12">12px</option>
                        <option value="14">14px</option>
                        <option value="16">16px</option>
                        <option value="18">18px</option>
                        <option value="20">20px</option>
                        <option value="24">24px</option>
                      </select>
                    </div>
                  )}
                  
                  {/* Editor Content */}
                  <div style={{ position: 'relative' }}>
                    {previewMode ? (
                      <div 
                        className="contract-editor preview-mode"
                        dangerouslySetInnerHTML={{ __html: generatePreview() }}
                      />
                    ) : (
                      <EditorContent 
                        editor={editor}
                        className="contract-editor"
                        style={{
                          pointerEvents: editMode === 'view' ? 'none' : 'auto',
                          opacity: editMode === 'view' ? 0.7 : 1
                        }}
                      />
                    )}
                    
                    {/* Autocomplete Suggestions */}
                    {showSuggestions && !previewMode && (editMode === 'edit' || editMode === 'create') && (
                      <div className="autocomplete-menu" style={{
                        position: 'absolute',
                        top: '100%',
                        left: '0',
                        backgroundColor: brandIdentity.colors.white,
                        border: `1px solid ${brandIdentity.colors.borderLight}`,
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 1500,
                        width: '280px',
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        <div style={{ 
                          padding: '8px 12px', 
                          borderBottom: `1px solid ${brandIdentity.colors.borderLight}`, 
                          fontSize: brandIdentity.typography.sizes.small, 
                          color: brandIdentity.colors.textLight 
                        }}>
                          Variables disponibles {suggestionQuery && `para "${suggestionQuery}"`}
                        </div>
                        {getFilteredVariables(suggestionQuery).slice(0, 10).map((variable) => (
                          <div
                            key={variable.token}
                            onClick={() => insertVariable(variable.token)}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: brandIdentity.typography.sizes.small,
                              color: brandIdentity.colors.textDark,
                              borderBottom: `1px solid ${brandIdentity.colors.borderLight}`
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = brandIdentity.colors.backgroundLight;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                            }}
                          >
                            {variable.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}


              {/* Loading States */}
              {loadingTemplate && (
                <div className="loading-overlay">
                  <div className="loading-spinner-small"></div>
                  <p>Cargando plantilla...</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Panel: Variables and Images Sidebar */}
        <div className="variables-sidebar-column card">
          <div className="sidebar-content">
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: brandIdentity.typography.sizes.large,
              fontWeight: brandIdentity.typography.weights.bold,
              color: brandIdentity.colors.textDark
            }}>
              Variables e Imágenes
            </h3>
            
            {/* Variables Section */}
            <div className="sidebar-section">
              <h4 style={{
                margin: '0 0 12px 0',
                fontSize: brandIdentity.typography.sizes.medium,
                fontWeight: brandIdentity.typography.weights.semibold,
                color: brandIdentity.colors.primary
              }}>
                Variables Disponibles
              </h4>
              <p style={{
                margin: '0 0 16px 0',
                fontSize: brandIdentity.typography.sizes.small,
                color: brandIdentity.colors.textLight
              }}>
                Haz clic para insertar campos. También puedes usar / o @ seguido del nombre.
              </p>
              
              <div className="variables-list">
                {Object.entries(VARIABLE_CATALOG).map(([category, variables]) => (
                  <div key={category} style={{ marginBottom: '16px' }}>
                    <h5 style={{
                      margin: '0 0 8px 0',
                      fontSize: brandIdentity.typography.sizes.small,
                      fontWeight: brandIdentity.typography.weights.semibold,
                      color: brandIdentity.colors.primary,
                      borderBottom: `1px solid ${brandIdentity.colors.borderLight}`,
                      paddingBottom: '4px'
                    }}>
                      {category.split(' (')[0]}
                    </h5>
                    <div style={{ display: 'grid', gap: '4px' }}>
                      {variables.map((variable) => (
                        <div
                          key={variable.token}
                          onClick={() => insertVariableFromSidebar(variable.token)}
                          style={{
                            padding: '6px 8px',
                            backgroundColor: brandIdentity.colors.backgroundLight,
                            border: `1px solid ${brandIdentity.colors.borderLight}`,
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: brandIdentity.typography.sizes.small,
                            color: brandIdentity.colors.textDark,
                            transition: 'all 0.2s ease',
                            userSelect: 'none'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = brandIdentity.colors.primary;
                            e.target.style.color = brandIdentity.colors.white;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = brandIdentity.colors.backgroundLight;
                            e.target.style.color = brandIdentity.colors.textDark;
                          }}
                        >
                          {variable.label}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Images Section */}
            <div className="sidebar-section" style={{ marginTop: '24px' }}>
              <h4 style={{
                margin: '0 0 12px 0',
                fontSize: brandIdentity.typography.sizes.medium,
                fontWeight: brandIdentity.typography.weights.semibold,
                color: brandIdentity.colors.primary
              }}>
                Imágenes Personalizadas
              </h4>
              
              {/* Image Upload Area */}
              <div 
                className="image-upload-area"
                style={{
                  border: `2px dashed ${isDraggingFile ? brandIdentity.colors.primary : brandIdentity.colors.borderLight}`,
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'center',
                  marginBottom: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: isDraggingFile ? brandIdentity.colors.backgroundLight : 'transparent'
                }}
                onClick={() => document.getElementById('image-upload-input').click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDraggingFile(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDraggingFile(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingFile(false);
                  const files = Array.from(e.dataTransfer.files);
                  handleImageUpload(files);
                }}
              >
                {uploadingImages ? (
                  <>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                    <p style={{
                      margin: 0,
                      fontSize: brandIdentity.typography.sizes.small,
                      color: brandIdentity.colors.primary
                    }}>
                      Procesando imágenes...
                    </p>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>📸</div>
                    <p style={{
                      margin: 0,
                      fontSize: brandIdentity.typography.sizes.small,
                      color: brandIdentity.colors.textLight
                    }}>
                      Haz clic o arrastra imágenes aquí
                    </p>
                    <p style={{
                      margin: '4px 0 0 0',
                      fontSize: brandIdentity.typography.sizes.xsmall,
                      color: brandIdentity.colors.textLight
                    }}>
                      PNG, JPG, SVG - Máx. 5MB
                    </p>
                  </>
                )}
              </div>
              
              <input
                id="image-upload-input"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files?.length > 0) {
                    handleImageUpload(e.target.files);
                    e.target.value = ''; // Reset input to allow same file selection
                  }
                }}
              />
              
              {/* Uploaded Images List */}
              <div className="uploaded-images-list">
                {uploadedImages.length > 0 && (
                  <>
                    <h5 style={{
                      margin: '0 0 8px 0',
                      fontSize: brandIdentity.typography.sizes.small,
                      fontWeight: brandIdentity.typography.weights.semibold,
                      color: brandIdentity.colors.textDark
                    }}>
                      Imágenes Cargadas ({uploadedImages.length})
                    </h5>
                    <p style={{
                      margin: '0 0 12px 0',
                      fontSize: brandIdentity.typography.sizes.xsmall,
                      color: brandIdentity.colors.textLight
                    }}>
                      Haz clic para insertar el placeholder en el documento
                    </p>
                  </>
                )}
                
                {uploadedImages.map((image, index) => (
                  <div
                    key={image.id}
                    className="uploaded-image-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      marginBottom: '8px',
                      background: brandIdentity.colors.backgroundLight,
                      border: `1px solid ${brandIdentity.colors.borderLight}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      userSelect: 'none'
                    }}
                    onClick={() => insertImagePlaceholder(image.placeholder)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = brandIdentity.colors.primary;
                      e.currentTarget.style.color = brandIdentity.colors.white;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = brandIdentity.colors.backgroundLight;
                      e.currentTarget.style.color = brandIdentity.colors.textDark;
                    }}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', `{{${image.placeholder}}}`);
                      e.currentTarget.style.opacity = '0.5';
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    title={`Haz clic para insertar: ${image.placeholder}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: brandIdentity.colors.white,
                        border: `1px solid ${brandIdentity.colors.borderLight}`
                      }}>
                        {image.type === 'image/svg+xml' ? (
                          <span style={{ fontSize: '12px' }}>SVG</span>
                        ) : (
                          <img
                            src={image.dataUrl}
                            alt={image.fileName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        )}
                      </div>
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: brandIdentity.typography.sizes.small,
                          fontWeight: brandIdentity.typography.weights.semibold,
                          color: 'inherit',
                          marginBottom: '2px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {image.placeholder}
                        </div>
                        <div style={{
                          fontSize: brandIdentity.typography.sizes.xsmall,
                          color: 'inherit',
                          opacity: 0.7,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {image.fileName} • {(image.size / 1024).toFixed(1)}KB
                          {image.originalSize !== image.size && (
                            <span> (orig: {(image.originalSize / 1024).toFixed(1)}KB)</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '12px', opacity: 0.7 }}>📋</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage(image.id);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '16px',
                          opacity: 0.7,
                          transition: 'opacity 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = '1'}
                        onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                        title="Eliminar imagen"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Asignación de Contrato */}
      {showAssignmentModal && (
        <div className="assignment-modal-overlay">
          <div className="assignment-modal-content">
            <div className="assignment-modal-header">
              <div className="assignment-modal-icon">
                🔗
              </div>
              <h3 style={{
                margin: '0 0 8px 0',
                color: brandIdentity.colors.textDark,
                fontSize: brandIdentity.typography.sizes.h2,
                fontWeight: brandIdentity.typography.weights.semibold
              }}>
                Asignar Contrato a Aliado
              </h3>
              <p style={{
                margin: 0,
                color: brandIdentity.colors.textLight,
                fontSize: brandIdentity.typography.sizes.small,
                lineHeight: '1.4'
              }}>
                Contrato seleccionado: <strong>{selectedContract?.name}</strong>
              </p>
            </div>
            
            <div className="assignment-modal-body">
              <div className="friendly-message">
                <div className="message-icon">
                  💡
                </div>
                <div className="message-content">
                  <h4 style={{
                    margin: '0 0 8px 0',
                    color: brandIdentity.colors.textDark,
                    fontSize: brandIdentity.typography.sizes.body,
                    fontWeight: brandIdentity.typography.weights.medium
                  }}>
                    ¡Casi listo para asignar tu contrato!
                  </h4>
                  <p style={{
                    margin: 0,
                    color: brandIdentity.colors.textLight,
                    fontSize: brandIdentity.typography.sizes.small,
                    lineHeight: '1.5'
                  }}>
                    Para asignar este contrato a una marca, necesitas ir a la sección de <strong>Aliados</strong>. 
                    Allí podrás seleccionar la marca específica y realizar la asignación del contrato.
                  </p>
                </div>
              </div>
              
              <div className="steps-preview">
                <div className="step-item">
                  <span className="step-number">1</span>
                  <span className="step-text">Selecciona una compañía</span>
                </div>
                <div className="step-item">
                  <span className="step-number">2</span>
                  <span className="step-text">Elige la marca a asignar</span>
                </div>
                <div className="step-item">
                  <span className="step-number">3</span>
                  <span className="step-text">Asigna el contrato</span>
                </div>
              </div>
            </div>
            
            <div className="assignment-modal-actions">
              <button 
                className="btn btn-outline"
                onClick={() => setShowAssignmentModal(false)}
                style={{
                  borderColor: brandIdentity.colors.primary,
                  color: brandIdentity.colors.primary
                }}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-primary"
                onClick={navigateToEntities}
                style={{
                  background: `linear-gradient(135deg, ${brandIdentity.colors.primary} 0%, ${brandIdentity.colors.primaryDark} 100%)`,
                  border: 'none',
                  fontWeight: brandIdentity.typography.weights.semibold
                }}
              >
                👥 Ir a Aliados
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles */}
      <style jsx>{`
        .contracts-viewer-container {
          min-height: 100vh;
        }

        .contracts-layout {
          display: grid;
          grid-template-columns: 350px 1fr 320px;
          gap: 1.5rem;
          align-items: start;
        }

        .contracts-list-column, .contract-details-column {
          background-color: #fff;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          height: calc(100vh - 140px);
          overflow-y: auto;
        }

        .column-title {
          margin: 0;
          color: #333;
          font-size: 1.25rem;
        }

        .contracts-list {
          list-style: none;
          padding: 0;
          margin: 1rem 0 0;
        }

        .contract-list-item {
          padding: 0.75rem 1rem;
          margin-bottom: 0.5rem;
          border-radius: 6px;
          cursor: pointer;
          transition: background-color 0.2s ease, color 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: #555;
          border: 1px solid transparent;
        }

        .contract-list-item:hover {
          background-color: #f0f4f8;
        }

        .contract-list-item.active {
          background-color: #e6effd;
          color: #0056b3;
          font-weight: 600;
          border-color: #6366F1;
        }

        .contract-item-content {
          flex-grow: 1;
          cursor: pointer;
        }

        .contract-name {
          font-weight: 600;
          margin-bottom: 4px;
        }

        .contract-meta {
          display: flex;
          gap: 8px;
          font-size: 0.85rem;
          color: #666;
        }

        .contract-version {
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 500;
        }

        .contract-actions {
          display: flex;
          gap: 0.5rem;
          flex-shrink: 0;
        }

        .contract-details-column {
          position: relative;
        }

        .empty-state-details {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          text-align: center;
          color: #777;
        }

        .empty-state-details .icon-lg {
          font-size: 4rem;
          margin-bottom: 1rem;
          display: inline-block;
        }

        .contract-info-section {
          margin-bottom: 2rem;
        }

        .contract-info-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #eee;
        }

        .contract-title {
          margin: 0;
          color: #1a2b4d;
        }

        .contract-actions-header {
          display: flex;
          gap: 0.5rem;
        }

        .contract-info-details {
          margin-bottom: 1rem;
        }

        .contract-info-card {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border: 1px solid ${brandIdentity.colors.borderLight};
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .info-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid ${brandIdentity.colors.primary};
        }

        .collapsible-header {
          justify-content: space-between;
          user-select: none;
          transition: all 0.2s ease;
        }

        .collapsible-header:hover {
          background: rgba(99, 102, 241, 0.05);
          margin: -8px;
          padding: 8px;
          border-radius: 8px;
        }

        .collapse-icon {
          font-size: 1.2rem;
          transition: transform 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 50%;
        }

        .collapsible-content {
          animation: slideDown 0.3s ease-out;
          overflow: hidden;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            max-height: 500px;
            transform: translateY(0);
          }
        }

        .info-icon {
          font-size: 2rem;
          filter: drop-shadow(0 2px 4px rgba(99, 102, 241, 0.2));
        }

        .info-content {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .info-row {
          background: white;
          border-radius: 8px;
          padding: 1rem;
          border: 1px solid rgba(99, 102, 241, 0.1);
          transition: all 0.2s ease;
        }

        .info-row:hover {
          border-color: ${brandIdentity.colors.primary};
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
        }

        .info-item-modern {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .info-icon-small {
          font-size: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 50%;
          flex-shrink: 0;
        }

        .info-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .info-label-modern {
          font-size: ${brandIdentity.typography.sizes.small};
          font-weight: ${brandIdentity.typography.weights.medium};
          color: ${brandIdentity.colors.textLight};
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .info-value-modern {
          font-size: ${brandIdentity.typography.sizes.medium};
          font-weight: ${brandIdentity.typography.weights.semibold};
          color: ${brandIdentity.colors.textDark};
          word-break: break-word;
        }

        .editor-controls {
          margin-bottom: 1.5rem;
        }

        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .editor-actions {
          display: flex;
          gap: 0.5rem;
        }

        .quick-actions-section {
          margin-bottom: 1rem;
          padding: 1rem 0;
        }

        .quick-actions-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border: 1px solid ${brandIdentity.colors.borderLight};
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .quick-actions-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .tiptap-editor-section {
          position: relative;
        }

        .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.9);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .variable-chip {
          background-color: #F3F4F6 !important;
          border: 1px solid #CBD5E1 !important;
          border-radius: 12px !important;
          padding: 2px 8px !important;
          margin: 0 2px !important;
          font-size: 14px !important;
          font-family: monospace !important;
          color: #374151 !important;
          display: inline-block !important;
          cursor: pointer !important;
          user-select: none !important;
          transition: all 0.2s ease !important;
        }
        
        .variable-chip:hover {
          background-color: #E5E7EB !important;
          transform: scale(1.05) !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        }
        
        .suggestion-highlight {
          background-color: #FEF3C7 !important;
          border-radius: 3px !important;
        }
        
        .contract-editor {
          min-height: 400px;
          padding: 24px;
          border: 1px solid #D1D5DB;
          border-radius: 8px;
          background: white;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          line-height: 1.6;
          outline: none;
          position: relative;
        }
        
        .contract-editor:empty:before {
          content: 'Escribe aquí el contenido de tu contrato...';
          color: #9CA3AF;
          font-style: italic;
          pointer-events: none;
          position: absolute;
          top: 24px;
          left: 24px;
        }
        
        .contract-editor:focus:empty:before {
          opacity: 0.7;
        }
        
        .contract-editor:focus-within {
          border-color: ${brandIdentity.colors.primary};
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        
        .preview-mode {
          background-color: #F9FAFB !important;
          border-style: dashed !important;
        }
        
        .contract-editor p {
          margin: 8px 0;
        }
        
        .contract-editor h1, .contract-editor h2, .contract-editor h3 {
          margin: 16px 0 8px 0;
          font-weight: 600;
        }
        
        .contract-editor table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
        }
        
        .contract-editor td, .contract-editor th {
          border: 1px solid #D1D5DB;
          padding: 8px;
          text-align: left;
        }
        
        .contract-editor th {
          background-color: #F9FAFB;
          font-weight: 600;
        }
        
        .contract-editor .page-break {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 16px;
          margin: 32px 0;
          border-top: 2px dashed #6366F1;
          border-bottom: 2px dashed #6366F1;
          background: #F0F9FF;
          color: #6366F1;
          font-weight: 600;
          font-size: 14px;
          border-radius: 4px;
          user-select: none;
          position: relative;
        }
        
        .contract-editor .page-break:hover {
          background: #DBEAFE;
        }
        
        @media print {
          .contract-editor .page-break {
            page-break-before: always;
            display: none !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            background: none !important;
          }
        }
        
        .preview-mode .page-break {
          page-break-before: always;
          display: block !important;
          height: 2px;
          background: linear-gradient(90deg, #6366F1 50%, transparent 50%);
          background-size: 20px 2px;
          border: none;
          margin: 20px 0;
          position: relative;
        }
        
        .preview-mode .page-break::before {
          content: 'Nueva página';
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          padding: 0 8px;
          font-size: 12px;
          color: #6366F1;
          font-weight: normal;
        }
        
        .toolbar-btn {
          padding: 6px 10px;
          border: 1px solid #D1D5DB;
          background: white;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          margin-right: 4px;
          transition: all 0.2s ease;
        }
        
        .toolbar-btn:hover {
          background-color: #F3F4F6;
        }
        
        .toolbar-btn.active {
          background-color: ${brandIdentity.colors.primary};
          color: white;
          border-color: ${brandIdentity.colors.primary};
        }

        .toolbar-btn.table-control {
          animation: slideIn 0.2s ease-in-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .editor-toolbar {
          display: flex;
          align-items: center;
          padding: 12px;
          background-color: #F9FAFB;
          border: 1px solid #E5E7EB;
          border-radius: 8px 8px 0 0;
          flex-wrap: wrap;
          gap: 4px;
        }

        .btn-icon {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.2rem;
          font-size: 1.1rem;
          line-height: 1;
          color: #666;
          transition: color 0.2s ease;
        }

        .btn-icon:hover {
          color: #333;
        }

        .btn-icon-danger:hover {
          color: #dc3545;
        }

        .loading-spinner-small {
          border: 3px solid #f3f3f3;
          border-top: 3px solid #0066cc;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          animation: spin 1s linear infinite;
          margin: 1rem auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .empty-state {
          color: #888;
          text-align: center;
          padding: 1rem;
        }

        .error-message {
          color: #dc3545;
          background: #f8d7da;
          border: 1px solid #f5c6cb;
          padding: 0.75rem;
          border-radius: 4px;
          margin: 1rem 0;
        }

        /* Estilos para el Modal de Asignación */
        .assignment-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          animation: fadeIn 0.3s ease-out;
        }
        
        .assignment-modal-content {
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          width: 90%;
          max-width: 520px;
          max-height: 90vh;
          overflow-y: auto;
          animation: slideUp 0.3s ease-out;
        }
        
        .assignment-modal-header {
          padding: 32px 32px 24px 32px;
          text-align: center;
          background: linear-gradient(135deg, ${brandIdentity.colors.backgroundLight} 0%, #f1f5f9 100%);
          border-radius: 16px 16px 0 0;
        }
        
        .assignment-modal-icon {
          font-size: 48px;
          margin-bottom: 16px;
          opacity: 0.9;
          display: inline-block;
          animation: bounce 2s infinite;
        }
        
        .assignment-modal-body {
          padding: 32px;
        }
        
        .friendly-message {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border: 1px solid #bae6fd;
          border-radius: 12px;
          margin-bottom: 24px;
        }
        
        .message-icon {
          font-size: 24px;
          flex-shrink: 0;
          margin-top: 2px;
        }
        
        .message-content {
          flex: 1;
        }
        
        .steps-preview {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .step-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #f8fafc;
          border-radius: 8px;
          border-left: 3px solid ${brandIdentity.colors.primary};
        }
        
        .step-number {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: ${brandIdentity.colors.primary};
          color: white;
          border-radius: 50%;
          font-size: 12px;
          font-weight: 600;
          flex-shrink: 0;
        }
        
        .step-text {
          color: ${brandIdentity.colors.textDark};
          font-size: 14px;
          font-weight: 500;
        }
        
        .assignment-modal-actions {
          padding: 24px 32px 32px 32px;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          border-top: 1px solid #e5e7eb;
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-8px);
          }
          60% {
            transform: translateY(-4px);
          }
        }

        /* Variables and Images Sidebar Styles */
        .variables-sidebar-column {
          background-color: #fff;
          border-radius: 8px;
          padding: 0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          height: calc(100vh - 140px);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .sidebar-content {
          padding: 1.5rem;
          overflow-y: auto;
          flex: 1;
        }

        .sidebar-section {
          margin-bottom: 24px;
        }

        .variables-list {
          max-height: 400px;
          overflow-y: auto;
        }

        .image-upload-area {
          border: 2px dashed #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          text-align: center;
          margin-bottom: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
          background: #fafafa;
        }

        .image-upload-area:hover {
          border-color: #6366F1;
          background-color: #f8fafc;
        }

        .uploaded-images-list {
          display: grid;
          gap: 8px;
        }

        .uploaded-image-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          cursor: grab;
        }

        .uploaded-image-item:active {
          cursor: grabbing;
        }

        .uploaded-image-item:hover {
          background: #e6effd;
          border-color: #6366F1;
        }

        @media (max-width: 1200px) {
          .contracts-layout {
            grid-template-columns: 1fr;
          }
          .contracts-list-column {
            height: auto;
            max-height: 40vh;
            margin-bottom: 1.5rem;
          }
          .contract-details-column {
            height: auto;
          }
          .variables-sidebar-column {
            height: auto;
            margin-top: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ContractsViewer;